import { NotificationsDialog } from "@/components/notifications/NotificationsDialog";
import {
  Bell,
  MessageSquareMore,
  Undo2,
  Briefcase,
} from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
  Separator,
  Button,
  Badge as BadgeUI,
} from "@/components/ui";

import {
  convertTimestampToTime,
  formatAvatarName,
  formatDate,
  truncateText,
} from "@/utils";
import {
  useAuth,
  useFetchNotificationsQuery,
  useGetUnreadMessagesCountQuery,
  usePermission,
  useUpdateNotificationMutation,
} from "@/hooks";
import { ReactNode, useState } from "react";
import { DialogResponsive } from "../common";
// import { hasAccess } from "@/helpers";

type NavbarProps = {
  backButton?: boolean;
};

export function Navbar({ backButton }: NavbarProps) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();
  const { signOut, user } = useAuth();
  const { data: notificationsData, isError: notificationsError } = useFetchNotificationsQuery({ per_page: "5", realtime: true });
  const unreadNotifications = notificationsData?.data.totalUnreadNotifications;
  const notificationCountKnown = !notificationsError && typeof unreadNotifications === "number" && Number.isInteger(unreadNotifications) && unreadNotifications >= 0;
  const notificationLabel = notificationCountKnown
    ? `Abrir notificações: ${unreadNotifications} não lidas`
    : notificationsError
      ? "Abrir notificações: não foi possível atualizar"
      : "Abrir notificações: contagem indisponível";
  // INB-A03: the Inbox count follows the notifications rule — a failed or
  // missing read is unknown, never "no unread messages".
  const { data: unreadMessagesData, isError: unreadMessagesError } = useGetUnreadMessagesCountQuery();
  const unreadMessages = unreadMessagesData?.unread;
  const unreadMessagesKnown = !unreadMessagesError && typeof unreadMessages === "number" && Number.isInteger(unreadMessages) && unreadMessages >= 0;
  const inboxLabel = unreadMessagesKnown
    ? `Abrir Inbox: ${unreadMessages} não lidas`
    : unreadMessagesError
      ? "Abrir Inbox: não foi possível contar as mensagens não lidas"
      : "Abrir Inbox: contagem indisponível";

  const buttonClassName =
    "size-11 shrink-0 rounded-xl md:size-10 border border-app-border bg-app-surface text-app-fg-muted shadow-premium-xs hover:border-brand/30 hover:bg-brand-soft hover:text-brand focus-visible:ring-brand";

  return (
    <header data-app-header className="relative z-20 min-h-[72px] w-full shrink-0 border-b border-app-border bg-app-surface py-3">
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 px-4">
        {!backButton && (
          <SidebarTrigger
            className="ml-0 size-11 p-0 shrink-0 rounded-xl md:size-10 text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg-strong"
            size="icon"
            aria-label="Alternar menu principal"
          />
        )}
        {backButton && (
          <Button
            variant="outline"
            className="text-app-fg"
            asChild
          >
            <Link to="/" aria-label="Voltar ao início">
              <Undo2 />
            </Link>
          </Button>
        )}
        <Separator orientation="vertical" className="mr-2 h-4 hidden" />
        <div className="contents md:flex md:items-center md:gap-2">
          <div className="ml-auto flex items-center gap-2">
          {/* {hasAccess(user, ["multi_chat"]) && (
            <div className="inline-block relative">
              <Badge count={15} />
              <Button variant="secondary" className={buttonClassName} asChild>
                <Link to="/inbox">
                  <MessageCircle size={20} />
                </Link>
              </Button>
            </div>
          )} */}
          <div className="inline-block relative">
            {unreadMessagesKnown ? (
              <Badge count={unreadMessages} />
            ) : (
              <span aria-hidden="true" className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-app-surface-muted text-xs font-bold text-app-fg">?</span>
            )}
            <Button variant="secondary" className={buttonClassName} asChild>
              <Link to="/inbox" aria-label={inboxLabel} title={inboxLabel}>
                {/* <MessageCircle size={20} /> */}
                <MessageSquareMore size={20} />
              </Link>
            </Button>
          </div>
          <div className="inline-block relative">
            {notificationCountKnown ? (
              <Badge count={unreadNotifications} />
            ) : (
              <span aria-hidden="true" className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-app-surface-muted text-xs font-bold text-app-fg">?</span>
            )}
            <NotificationsDialog key={user?.id}><Button variant="secondary" className={buttonClassName} aria-label={notificationLabel} title={notificationLabel}><Bell size={20} /></Button></NotificationsDialog>
          </div>
          {/* <Popover>
            <PopoverTrigger>
              <div className="inline-block relative">
                {notificationsData && (
                  <Badge
                    count={notificationsData.data.totalUnreadNotifications || 0}
                  />
                )}
                <Button variant="secondary" className={buttonClassName}>
                  <Link to="/notifications">
                    <Bell size={20} />
                  </Link>
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent className="mr-10 mt-2 text-sm border-zinc-100 shadow-zinc-200/75 shadow-2xl w-96">
              <header className="w-full pb-3 border-b border-zinc-100">
                <h2 className="text-sm font-semibold text-zinc-700">
                  {t("LABEL.notifications")}
                </h2>
              </header>
              <Notifications notifications={notificationsData} />
            </PopoverContent>
          </Popover> */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Abrir menu da conta" className="flex size-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 md:size-10">
              <Avatar className="size-10 border border-brand/40">
                <AvatarImage src={user?.avatar || ""} />

                <AvatarFallback className="bg-brand-soft text-brand font-semibold">
                  {formatAvatarName(user?.name || "")}
                </AvatarFallback>
              </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="bottom"
              sideOffset={5}
              className="text-app-fg-muted"
            >
              <DropdownMenuLabel>{t("LABEL.my account")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/my-business" className="flex items-center gap-2 text-brand font-medium">
                  <Briefcase className="w-4 h-4" />
                  <span>Meu Negócio</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <div className="flex items-center gap-2">
                  <ReactCountryFlag countryCode="BR" />
                  <span>{t("LABEL.language")}</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings/account">
                  {t("LABEL.settings")}
                </Link>
              </DropdownMenuItem>
              {checkPermission("role", "view role") && (
                <DropdownMenuItem asChild>
                  <Link to="/activity-logs">{t("LABEL.activity logs")}</Link>
                </DropdownMenuItem>
              )}
              {user && user.type === "admin" && (
                <DropdownMenuItem asChild>
                  {/* SCRUM-208 (audit C-04): the plan is read and changed in
                      settings; buying happens on /onboarding/billing. */}
                  <Link
                    to="/settings/usage"
                    className="flex items-center justify-between"
                  >
                    {t("LABEL.plan upgrade")}
                    <BadgeUI className="bg-brand-soft text-brand hover:bg-brand-soft text-xs">
                      {user.is_trial ? (
                        <span>Teste</span>
                      ) : (
                        user.subscription && (
                          <span>{truncateText(user.subscription.name, 3)}</span>
                        )
                      )}
                    </BadgeUI>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link
                  to={`https://www.clinisharp.com.br/docs?key=${import.meta.env.VITE_DOCS_ACCESS_KEY
                    }`}
                  target="_blank"
                >
                  {t("LABEL.user manual")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={signOut}>
                {t("LABEL.to go out")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

type Props = {
  count: number;
};

function Badge({ count }: Props) {
  const displayCount = count > 99 ? "99+" : count;
  return (
    <span
      aria-hidden="true"
      data-status={count}
      className={`data-[status=0]:hidden absolute bg-app-danger text-white text-xs font-bold rounded-full flex items-center justify-center -top-2 -right-2 ${displayCount.toString().length > 2 ? "px-1 py-1" : "w-5 h-5"
        }`}
    >
      {displayCount}
    </span>
  );
}

// type NotificationsProps = {
//   notifications?: NotificationsResponse;
// };

// function Notifications({
//   notifications: notificationsData,
// }: NotificationsProps) {
//   const { t } = useTranslation();

//   const filteredUnreadNotifications = notificationsData?.items
//     ?.filter((notification) => notification.read_at === null);

//   const filteredReadNotifications = notificationsData?.items
//     ?.filter((notification) => typeof notification.read_at === "string");

//   const lastPage = notificationsData?.data?.last_page || 1;

//   const tabs = [
//     {
//       label: "Não lidas",
//       value: "unread",
//     },
//     {
//       label: "Lidas",
//       value: "read",
//     },
//   ];

//   return (
//     <main className="w-full mt-2">
//       <Tabs
//         defaultValue={
//           notificationsData &&
//             notificationsData.data.totalUnreadNotifications &&
//             notificationsData.data.totalUnreadNotifications > 0
//             ? "unread"
//             : "read"
//         }
//         className="w-full"
//       >
//         <TabsList className="w-full justify-start py-0 bg-transparent border-b rounded-none">
//           {tabs.map((tab, index) => (
//             <TabsTrigger
//               key={index}
//               value={tab.value}
//               className="h-full data-[state=active]:bg-transparent border-b data-[state=active]:border-teal-600 data-[state=active]:text-zinc-600 data-[state=active]:shadow-none rounded-none flex-1"
//             >
//               {tab.label}
//             </TabsTrigger>
//           ))}
//         </TabsList>

//         <TabsContent value="unread" className="py-3">
//           <div className="flex flex-col space-y-3">
//             {filteredUnreadNotifications?.map((notification, index) => (
//               <NotificationItem
//                 key={index}
//                 id={notification.id}
//                 title={notification.title}
//                 description={notification.message}
//                 date={notification.timestamp}
//                 time={notification.timestamp}
//                 unread
//               />
//             ))}

//             {lastPage > 1 && (
//               <Link
//                 to="/notifications?filter=unread"
//                 className="text-center p-3 bg-teal-700 text-white font-medium rounded-lg"
//               >
//                 {t("LABEL.view more")}
//               </Link>
//             )}
//           </div>
//         </TabsContent>

//         <TabsContent value="read" className="py-3">
//           <div className="flex flex-col space-y-3">
//             {filteredReadNotifications?.map((notification, index) => (
//               <NotificationItem
//                 key={index}
//                 id={notification.id}
//                 title={notification.title}
//                 description={notification.message}
//                 date={notification.timestamp}
//                 time={notification.timestamp}
//               />
//             ))}

//             {lastPage > 1 && (
//               <Link
//                 to="/notifications?filter=read"
//                 className="text-center p-3 bg-teal-700 text-white font-medium rounded-lg"
//               >
//                 {t("LABEL.view more")}
//               </Link>
//             )}
//           </div>
//         </TabsContent>
//       </Tabs>
//     </main>
//   );
// }


type NotificationItemProps = {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  unread?: boolean;
  actionPath?: string | null;
  actionLabel?: string | null;
};

// function NotificationItem(notification: NotificationItemProps) {
//   return (
//     <ShowNotifiction {...notification}>
//       <div
//         data-unread={notification.unread}
//         className="flex items-start cursor-pointer p-2 rounded-lg gap-2 relative data-[unread=true]:bg-teal-100/50"
//       >
//         <div className="space-y-1">
//           <h4 className="font-semibold text-zinc-600">{notification.title}</h4>
//           <p className="text-xs text-zinc-500">
//             {truncateText(notification.description, 40)}
//           </p>
//         </div>
//         <div className="space-y-1 text-zinc-500 text-xs text-right">
//           <p>{formatDate(notification.date, false, true)}</p>
//           <p>{convertTimestampToTime(notification.time)}</p>
//         </div>

//         {notification.unread && (
//           <span className="absolute flex h-2 w-2 top-5 right-4">
//             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-600 opacity-75" />
//             <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600" />
//           </span>
//         )}
//       </div>
//     </ShowNotifiction>
//   );
// }

type ShowNotificationProps = NotificationItemProps & {
  children: ReactNode;
};

export function ShowNotifiction({
  children,
  ...notification
}: ShowNotificationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { mutateAsync } = useUpdateNotificationMutation();

  function handleOpen(open: boolean) {
    if (open) {
      setTimeout(async () => {
        await mutateAsync(notification.id);
      }, 100);
    }
    setOpen(open);
  }

  async function handleAction() {
    await mutateAsync(notification.id);
    setOpen(false);
    if (notification.actionPath) {
      navigate(notification.actionPath);
    }
  }
  return (
    <DialogResponsive
      title={t("LABEL.notifications")}
      button={children}
      defaultOpen={open}
      setDefaultOpen={handleOpen}
    >
      <main className="space-y-3 text-center">
        <h2 className="text-base font-bold text-zinc-700">
          {notification.title}
        </h2>
        <div className="space-y-2 text-sm text-zinc-700">
          <p>{notification.description}</p>
          <p>{formatDate(notification.date, false, true)}</p>
          <p>{convertTimestampToTime(notification.time)}</p>
        </div>
        {notification.actionPath && (
          <Button className="w-full" onClick={handleAction}>
            {notification.actionLabel || "Abrir"}
          </Button>
        )}
      </main>
    </DialogResponsive>
  );
}
